<template>
  <div class="counting-game">
    <LevelComplete
      v-if="isGameComplete"
      message="Awesome!"
    />
    <div v-if="!isGameComplete">
      <h1
        class="target-number"
        @click="speakOutLoudTarget"
      >
        {{ targetNumber }} {{ targetObject }}
      </h1>
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>

      <!-- Slots for the target objects -->
      <div class="slots">
        <div
          v-for="(slotObject, index) in slotObjects"
          :key="index"
          :ref="`slot-${index}`"
          class="slot"
          @dragover.prevent
          @drop="handleDrop($event, index)"
        >
          <span v-if="slotObject">{{ slotObject }}</span>
        </div>
      </div>

      <!-- Mixed objects at the bottom -->
      <div class="grid">
        <div
          v-for="(object, index) in mixedObjects"
          :key="index"
          class="object-draggable"
          :style="{
            visibility:
              draggedObject === object && draggedIndex === index
                ? 'hidden'
                : 'visible',
          }"
          draggable="true"
          @dragstart="handleDrag($event, object)"
          @click="speakOutLoudObject(object)"
          @touchstart="handleTouchStart($event, object, index)"
          @touchmove="handleTouchMove($event)"
          @touchend="handleTouchEnd($event)"
        >
          {{ object }}
        </div>
      </div>
    </div>

    <!-- Ghost element for dragging on mobile -->
    <div
      v-if="draggedGhost"
      class="dragged-ghost"
      :style="draggedGhostStyle"
    >
      {{ draggedGhost }}
    </div>
  </div>
</template>

<script>
import { speechMixin } from "@/mixins/speechMixin.js";

import LevelComplete from "../LevelComplete.vue";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      maxCount: 1,
      targetNumber: 0,
      targetObject: "",
      objects: ["🍎", "🍌", "🍇", "🍉", "🍓"],
      objectNames: { // Mapping emojis to fruit names
        "🍎": "apple",
        "🍌": "banana",
        "🍇": "grape",
        "🍉": "watermelon",
        "🍓": "strawberry"
      },
      objectCounts: {},
      message: "",
      isGameComplete: false,
      draggedObject: null,
      draggedIndex: null,
      draggedGhost: null,
      draggedGhostStyle: {},
      completedObjects: new Set(),
      currentCount: 0,
      slotObjects: [], // Track the fruits in the slots
      mixedObjects: [], // Mixed objects for dragging

      // Properties for touch offset
      touchOffsetX: 0,
      touchOffsetY: 0,
    };
  },
  mounted() {
    this.initializeObjectCounts();
    this.setupVoices();
    this.setupGame();
  },
  methods: {
    initializeObjectCounts() {
      this.objects.forEach((object) => {
        this.objectCounts[object] = 0;
      });
    },
    setupGame() {
      if (this.completedObjects.size === this.objects.length * this.maxCount) {
        this.isGameComplete = true;
        this.speakOutLoud("Awesome!");
        setTimeout(() => {
          this.$router.push("/numbers");
        }, 4000);
        return;
      }

      const availableObjects = this.objects.filter(
        (obj) => this.objectCounts[obj] < this.maxCount
      );

      if (availableObjects.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableObjects.length);
        this.targetObject = availableObjects[randomIndex];
      } else {
        console.error("No available objects to select.");
        return;
      }

      this.targetNumber = Math.floor(Math.random() * 9) + 1;
      this.speakOutLoud(`${this.targetNumber} ${this.getTargetObjectText(this.targetNumber)}`);
      this.currentCount = 0;
      this.message = "";
      this.slotObjects = Array(this.targetNumber).fill(null); // Initialize slots

      // Prepare mixed objects
      const targetObjects = Array(this.targetNumber).fill(this.targetObject);
      const otherObjects = this.objects
        .filter((obj) => obj !== this.targetObject)
        .flatMap((obj) => Array(Math.floor(Math.random() * 3)).fill(obj));
      this.mixedObjects = [...targetObjects, ...otherObjects].sort(
        () => Math.random() - 0.5
      );
    },
    getTargetObjectText(number) {
      const singular = this.objectNames[this.targetObject] || "object";
      if (number > 1) {
        return this.pluralize(singular);
      }
      return singular;
    },
    pluralize(name) {
      // Handle irregular plurals if needed
      if (name === "grape") return "grapes";
      if (name === "strawberry") return "strawberries";
      return name + "s";
    },
    speakOutLoudTarget() {
      this.speakOutLoud(`${this.targetNumber} ${this.getTargetObjectText(this.targetNumber)}`);
    },
    speakOutLoudObject(object) {
      this.speakOutLoud(this.objectNames[object] || "object");
    },
    handleDrag(event, object) {
      event.dataTransfer.setData("text/plain", object);
    },
    handleDrop(event, index) {
      const droppedObject = event.dataTransfer.getData("text/plain");

      if (droppedObject === this.targetObject) {
        if (!this.slotObjects[index]) {
          this.slotObjects.splice(index, 1, droppedObject); // Place fruit in slot
          this.removeFromMixedObjects(droppedObject); // Remove from draggable fruits
          this.currentCount++;
          this.message = "✅";

          // speakOutLoud the current count aloud
          this.speakOutLoud(this.currentCount.toString());

          if (this.currentCount >= this.targetNumber) {
            this.objectCounts[droppedObject]++;
            this.completedObjects.add(
              droppedObject + this.objectCounts[droppedObject]
            );
            setTimeout(() => {
              this.setupGame();
            }, 2000);
          }
        } else {
          this.message = "❌ Slot already filled!";
        }
      } else {
        this.message = "❌ Try again!";
      }
    },
    removeFromMixedObjects(object) {
      const index = this.mixedObjects.indexOf(object);
      if (index > -1) {
        this.mixedObjects.splice(index, 1);
      }
    },
    handleTouchStart(event, object, index) {
      this.draggedObject = object;
      this.draggedIndex = index;
      this.draggedGhost = object;

      const touch = event.touches[0];

      // Get the bounding rect of the target element
      const targetEl = event.target;
      const rect = targetEl.getBoundingClientRect();

      // Calculate offset between touch point and the element's top-left corner
      this.touchOffsetX = touch.clientX - rect.left;
      this.touchOffsetY = touch.clientY - rect.top;

      // Set initial position of the ghost element
      this.draggedGhostStyle = {
        position: "fixed",
        left: `${touch.clientX - this.touchOffsetX}px`,
        top: `${touch.clientY - this.touchOffsetY}px`,
        width: `${rect.width}px`, // Ensure the ghost has the same dimensions
        height: `${rect.height}px`,
        zIndex: 1000,
      };
    },
    handleTouchMove(event) {
      event.preventDefault();
      const touch = event.touches[0];
      this.draggedGhostStyle.left = `${touch.clientX - this.touchOffsetX}px`;
      this.draggedGhostStyle.top = `${touch.clientY - this.touchOffsetY}px`;
    },
    handleTouchEnd(event) {
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;

      // Check each slot to see if touch ended over it
      for (let i = 0; i < this.slotObjects.length; i++) {
        const slotRef = this.$refs[`slot-${i}`];
        if (slotRef && slotRef.length > 0) {
          const slotEl = slotRef[0];
          const rect = slotEl.getBoundingClientRect();
          if (
            touchEndX >= rect.left &&
            touchEndX <= rect.right &&
            touchEndY >= rect.top &&
            touchEndY <= rect.bottom
          ) {
            this.handleDrop(
              { dataTransfer: { getData: () => this.draggedObject } },
              i
            );
            break;
          }
        }
      }

      // Reset dragging variables
      this.draggedObject = null;
      this.draggedIndex = null;
      this.draggedGhost = null;
      this.draggedGhostStyle = {};
    },
  },
};
</script>

<style scoped>
.progress {
  text-align: center;
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.slots {
  max-width: 500px;
  margin: auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 1rem;
  margin-bottom: 30px;
}

.slot {
  width: 80px;
  height: 80px;
  border: 2px dashed gray;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: lightgray;
  border-radius: 10px;
  font-size: 2rem; /* Adjust font size for the fruit */
}

.grid {
  max-width: 500px;
  margin: auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 1rem;
}

.target-number {
  font-size: 4rem;
  color: #ff4081;
  text-align: center;
  cursor: pointer;
}

.object-draggable {
  cursor: grab;
  border-radius: 10px;
  padding: 20px;
  font-size: 2rem;
  background-color: #81d4fa;
  font-weight: bold;
  text-align: center;
  transition: transform 0.3s;
  box-shadow: -2px 2px 2px 2px lightgray;
}

.object-draggable:active {
  cursor: grabbing;
}

.object-draggable:hover {
  transform: scale(1.1);
}

.dragged-ghost {
  pointer-events: none;
  position: fixed;
  border-radius: 10px;
  padding: 20px;
  font-size: 2rem;
  background-color: #81d4fa;
  font-weight: bold;
  text-align: center;
  box-shadow: -2px 2px 2px 2px lightgray;
}

.message {
  font-size: 3rem;
  font-weight: bold;
  text-align: center;
}
</style>